/**
 * SkillSlotsHUD — icon-based hotbar showing four skill slots at the bottom of the screen.
 *
 * Each slot renders: a spritesheet icon downscaled to SLOT_SIZE, a depleting arc ring showing
 * cooldown progress (red → cyan as it nears zero), a ready flash when the cooldown expires,
 * and a cyan ring while the skill is active. The icon dims during cooldown so the ring is the
 * sole visual indicator — no text overlay.
 *
 * Rings are rendered via Canvas2D (Phaser.Textures.CanvasTexture) for smooth antialiased arcs.
 * Governed by ADR-077.
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../utils/Constants';
import { crispText, crispRoundedRect, CRISP_SCALE } from './CrispUtil';
import { Font, Hex, Color } from './Theme';
import { SkillIconRegistry } from './SkillIconRegistry';

const SLOT_COUNT    = 4;
const SLOT_SIZE     = 28;    // icon render size (logical px)
const SLOT_SPACING  = 62;    // center-to-center
const BOTTOM_MARGIN = 18;    // gap from screen bottom to icon bottom edge
const SLOTS_PAD     = 14;    // underlay padding around the slot row

// Derived positions
const SLOT_CENTER_Y = GAME_HEIGHT - BOTTOM_MARGIN - SLOT_SIZE / 2;

// Slot underlay (skill-ring medallion)
const SLOT_UNDERLAY_SIZE = SLOT_SIZE + 12;  // 42px — stone bezel frames icon

// Cooldown ring — circular arc tracing the medallion stone groove
const RING_RADIUS    = SLOT_UNDERLAY_SIZE / 2 - 2;  // sits just inside the outer bezel
const RING_THICKNESS = 3;
// Canvas must contain the ring + halos (outer edge + 2px breathing room)
const RING_CANVAS_SIZE = Math.ceil((RING_RADIUS + RING_THICKNESS / 2 + 2) * 2);

const RING_COLOR_FULL  = 0xff2222;           // red   — cooldown just started (fraction = 1)
const RING_COLOR_MID   = 0xffcc00;           // yellow — halfway through cooldown
const RING_COLOR_READY = 0x44ff44;           // green  — cooldown nearly expired (fraction → 0)

// Icon states
const ICON_ALPHA_READY    = 1.0;
const ICON_ALPHA_COOLDOWN = 0.35;

// Active ring
const ACTIVE_TINT         = 0x88ccff;
const ACTIVE_BORDER_COLOR = Color.cyan;

// Ready flash
const FLASH_ALPHA_START = 0.7;
const FLASH_DURATION_MS = 280;

export interface SlotState {
  skillId: string | null;
  active: boolean;
  cooldown: number;       // ms remaining
  totalCooldown: number;  // ms total (for ring fraction)
  steps?: number;         // step count for step-based skills (stealth)
}

interface SlotObjects {
  slotUnderlay:  Phaser.GameObjects.Image;
  icon:          Phaser.GameObjects.Image;
  ringCanvas:    Phaser.Textures.CanvasTexture;
  ringImage:     Phaser.GameObjects.Image;
  flashRect:     Phaser.GameObjects.Rectangle;
  cooldownText:  Phaser.GameObjects.Text;   // remaining seconds, shown during cooldown
  stepsText:     Phaser.GameObjects.Text;
}

/** Renders four icon-based skill slots with smooth Canvas2D ring cooldown indicators. */
export class SkillSlotsHUD {
  private scene: Phaser.Scene;
  private slotObjs: SlotObjects[] = [];
  private slots: SlotState[] = [];
  private prevCooldown: number[] = [];
  private peakCooldown: number[] = [];   // snapshotted when a slot enters cooldown
  private bg: Phaser.GameObjects.Image;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const totalWidth = (SLOT_COUNT - 1) * SLOT_SPACING;
    const startX = (GAME_WIDTH - totalWidth) / 2;

    const bgWidth   = totalWidth + SLOT_SIZE + SLOTS_PAD * 2;
    const bgHeight  = SLOT_SIZE + SLOTS_PAD * 2;
    this.bg = crispRoundedRect(scene,
      GAME_WIDTH / 2, SLOT_CENTER_Y,
      bgWidth, bgHeight,
      6, Color.black, 0.35,
    ).setDepth(99).setVisible(false);

    for (let i = 0; i < SLOT_COUNT; i++) {
      const x = startX + i * SLOT_SPACING;

      const slotUnderlay = scene.add.image(x, SLOT_CENTER_Y, 'skill-ring-lighter')
        .setDisplaySize(SLOT_UNDERLAY_SIZE, SLOT_UNDERLAY_SIZE)
        .setDepth(99.5);
      slotUnderlay.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

      const { treeName, frameIndex } = SkillIconRegistry.get('__empty__');
      const icon = scene.add.image(x, SLOT_CENTER_Y, `skill-icons-${treeName}`, frameIndex)
        .setDisplaySize(SLOT_SIZE, SLOT_SIZE)
        .setDepth(100)
        .setVisible(false);

      // Canvas2D-backed ring — native antialiased arcs, rendered at CRISP_SCALE resolution
      const canvasPx = Math.ceil(RING_CANVAS_SIZE * CRISP_SCALE);
      const ringCanvas = scene.textures.createCanvas(`ring-slot-${i}`, canvasPx, canvasPx)!;
      const ringImage = scene.add.image(x, SLOT_CENTER_Y, `ring-slot-${i}`)
        .setDisplaySize(RING_CANVAS_SIZE, RING_CANVAS_SIZE)
        .setDepth(101)
        .setVisible(false);

      const flashRect = scene.add.rectangle(x, SLOT_CENTER_Y, SLOT_SIZE, SLOT_SIZE, 0xffffff, 0)
        .setDepth(102);

      const cooldownText = crispText(scene, x, SLOT_CENTER_Y, '', {
        fontFamily: Font.family,
        fontSize: Font.sizeXs,
        color: '#ffffff',
      }).setOrigin(0.5).setDepth(103).setVisible(false);

      const stepsText = crispText(scene, x, SLOT_CENTER_Y, '', {
        fontFamily: Font.family,
        fontSize: Font.sizeXs,
        color: Hex.cyan,
      }).setOrigin(0.5).setDepth(103).setVisible(false);

      this.slotObjs.push({ slotUnderlay, icon, ringCanvas, ringImage, flashRect, cooldownText, stepsText });
      this.slots.push({ skillId: null, active: false, cooldown: 0, totalCooldown: 0 });
      this.prevCooldown.push(0);
      this.peakCooldown.push(0);
    }
  }

  /** Update stored state for a single slot. */
  setSlot(index: number, state: SlotState): void {
    if (index < 0 || index >= SLOT_COUNT) return;
    this.slots[index] = state;
  }

  /** Redraw all slots. Called every frame from UIScene.update(). */
  draw(): void {
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot   = this.slots[i];
      const objs   = this.slotObjs[i];
      const prevCd = this.prevCooldown[i];

      if (!slot.skillId) {
        this.drawEmpty(objs);
      } else {
        this.syncIcon(objs.icon, slot.skillId);

        if (slot.active) {
          this.drawActive(objs, slot);
        } else if (slot.cooldown > 0) {
          // Snapshot peak when cooldown first starts (prevCd was 0); use as denominator.
          if (prevCd === 0) {
            this.peakCooldown[i] = slot.totalCooldown > 0 ? slot.totalCooldown : slot.cooldown;
          }
          this.drawCooldown(objs, slot, this.peakCooldown[i]);
        } else {
          this.drawReady(objs);
          if (prevCd > 0) this.fireFlash(objs);
        }
      }

      this.prevCooldown[i] = slot.cooldown;
    }
  }

  /** Destroy all Phaser objects and free resources. */
  destroy(): void {
    for (let i = 0; i < this.slotObjs.length; i++) {
      const objs = this.slotObjs[i];
      objs.slotUnderlay.destroy();
      objs.icon.destroy();
      objs.ringImage.destroy();
      objs.flashRect.destroy();
      objs.cooldownText.destroy();
      objs.stepsText.destroy();
      this.scene.textures.remove(`ring-slot-${i}`);
    }
    this.slotObjs = [];
    this.bg.destroy();
  }

  // ---------------------------------------------------------------------------
  // Private draw helpers
  // ---------------------------------------------------------------------------

  private drawEmpty(objs: SlotObjects): void {
    objs.icon.setVisible(false);
    objs.ringImage.setVisible(false);
    objs.cooldownText.setVisible(false);
    objs.stepsText.setVisible(false);
  }

  private drawReady(objs: SlotObjects): void {
    objs.icon.setVisible(true).clearTint().setAlpha(ICON_ALPHA_READY);
    objs.ringImage.setVisible(false);
    objs.cooldownText.setVisible(false);
    objs.stepsText.setVisible(false);
  }

  private drawCooldown(objs: SlotObjects, slot: SlotState, peak: number): void {
    const fraction = peak > 0 ? Math.min(slot.cooldown / peak, 1) : 1;
    objs.icon.setVisible(true).clearTint().setAlpha(ICON_ALPHA_COOLDOWN);
    objs.stepsText.setVisible(false);
    const ringColor = lerpColorTriple(RING_COLOR_READY, RING_COLOR_MID, RING_COLOR_FULL, fraction);
    this.paintRing(objs, fraction, ringColor, 0.5);

    const label = formatCooldown(slot.cooldown);
    if (objs.cooldownText.text !== label) objs.cooldownText.setText(label);
    objs.cooldownText.setColor('#' + ringColor.toString(16).padStart(6, '0'));
    objs.cooldownText.setVisible(true);
  }

  private drawActive(objs: SlotObjects, slot: SlotState): void {
    objs.icon.setVisible(true).setTint(ACTIVE_TINT).setAlpha(ICON_ALPHA_READY);
    objs.cooldownText.setVisible(false);

    this.paintRing(objs, 1.0, ACTIVE_BORDER_COLOR, 0.45);

    if (slot.steps !== undefined) {
      const t = String(slot.steps);
      if (objs.stepsText.text !== t) objs.stepsText.setText(t);
      objs.stepsText.setVisible(true);
    } else {
      objs.stepsText.setVisible(false);
    }
  }

  /** Update icon texture+frame if the skill assignment changed. */
  private syncIcon(icon: Phaser.GameObjects.Image, skillId: string): void {
    const { treeName, frameIndex } = SkillIconRegistry.get(skillId);
    const key = `skill-icons-${treeName}`;
    if (icon.texture.key !== key || icon.frame.name !== String(frameIndex)) {
      icon.setTexture(key, frameIndex);
    }
  }

  /**
   * Draw a ring arc to the Canvas2D-backed texture and upload it to the GPU.
   * fraction = 1.0 → full circle; fraction < 1 → clockwise arc from 12 o'clock.
   * Soft 1px halos on the inner and outer edges at 25% opacity for smooth falloff.
   */
  private paintRing(
    objs: SlotObjects,
    fraction: number,
    color: number,
    alpha: number,
  ): void {
    if (fraction <= 0) {
      objs.ringImage.setVisible(false);
      return;
    }

    const S    = CRISP_SCALE;
    const half = (RING_CANVAS_SIZE / 2) * S;
    const end  = -Math.PI / 2 + fraction * Math.PI * 2;
    const ctx  = objs.ringCanvas.context;

    ctx.clearRect(0, 0, objs.ringCanvas.width, objs.ringCanvas.height);
    ctx.lineCap = 'round';

    // Soft halo — 1px strokes just inside and outside the ring at 25% opacity
    ctx.lineWidth   = S;
    ctx.strokeStyle = colorToCss(color, alpha * 0.5);
    ctx.beginPath();
    ctx.arc(half, half, (RING_RADIUS - RING_THICKNESS / 2 - 0.5) * S, -Math.PI / 2, end);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(half, half, (RING_RADIUS + RING_THICKNESS / 2 + 0.5) * S, -Math.PI / 2, end);
    ctx.stroke();

    // Main ring
    ctx.lineWidth   = RING_THICKNESS * S;
    ctx.strokeStyle = colorToCss(color, alpha);
    ctx.beginPath();
    ctx.arc(half, half, RING_RADIUS * S, -Math.PI / 2, end);
    ctx.stroke();

    objs.ringCanvas.refresh();
    objs.ringImage.setVisible(true);
  }

  /** White flash rect tweened 0.7 → 0 over 280ms when a slot transitions to ready. */
  private fireFlash(objs: SlotObjects): void {
    objs.flashRect.setAlpha(FLASH_ALPHA_START);
    this.scene.tweens.add({
      targets: objs.flashRect,
      alpha: 0,
      duration: FLASH_DURATION_MS,
      ease: 'Sine.Out',
    });
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Linear interpolation between two packed RGB colors. t=0 → from, t=1 → to. */
/** Three-stop color interpolation: t=0 → a, t=0.5 → b, t=1 → c. */
function lerpColorTriple(a: number, b: number, c: number, t: number): number {
  return t < 0.5 ? lerpColor(a, b, t * 2) : lerpColor(b, c, (t - 0.5) * 2);
}

function lerpColor(from: number, to: number, t: number): number {
  const r1 = (from >> 16) & 0xff, g1 = (from >> 8) & 0xff, b1 = from & 0xff;
  const r2 = (to   >> 16) & 0xff, g2 = (to   >> 8) & 0xff, b2 = to   & 0xff;
  return (
    (Math.round(r1 + (r2 - r1) * t) << 16) |
    (Math.round(g1 + (g2 - g1) * t) << 8)  |
     Math.round(b1 + (b2 - b1) * t)
  );
}

/**
 * Format remaining cooldown ms as a compact label.
 * ≥1s: whole seconds via Math.ceil so each number holds for a full second ("3"→"2"→"1").
 * <1s: one decimal for precision in the final stretch ("0.9"→"0.8"→…).
 */
function formatCooldown(ms: number): string {
  const s = ms / 1000;
  if (s >= 1) return String(Math.ceil(s));
  return s.toFixed(1);
}

/** Convert a packed RGB number to a CSS color string. */
function colorToCss(color: number, alpha: number = 1): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8)  & 0xff;
  const b =  color        & 0xff;
  return alpha < 1 ? `rgba(${r},${g},${b},${alpha})` : `rgb(${r},${g},${b})`;
}
